import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const userCred = await signInWithEmailAndPassword(auth, email, password);
            const user = userCred.user;

            if (!user) {
                setError("Invalid credentials. Please try again.");
                setLoading(false);
                return;
            }

            // ✅ OTP-based verification already done during signup
            // no need to check user.emailVerified anymore

            setLoading(false);
            navigate("/"); // redirect to homepage or dashboard
        } catch (err) {
            console.error("Login error:", err);

            // Custom, user-friendly messages
            if (err.code === "auth/invalid-credential" || err.code === "auth/invalid-login-credentials") {
                setError("Invalid email or password. Please try again.");
            } else if (err.code === "auth/user-not-found") {
                setError("No account found with that email. Please sign up first.");
            } else if (err.code === "auth/wrong-password") {
                setError("Incorrect password. Try again.");
            } else if (err.code === "auth/too-many-requests") {
                setError("Too many failed attempts. Please wait and try again later.");
            } else {
                setError("Login failed. Please try again.");
            }
        }
    };

    const handleForgotPassword = () => {
        navigate("/forgot-password");
    };

    return (
        <div className="flex items-center justify-center min-h-[85vh] bg-gray-50 px-4">
            <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
                <h2 className="text-2xl font-bold text-center text-indigo-600 mb-6">
                    Sign In
                </h2>

                <form onSubmit={handleLogin} className="space-y-4">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                            placeholder="Enter your email"
                        />
                    </div>

                    {/* Password */}
                    <div className="relative">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Password
                        </label>
                        <input
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none pr-10"
                            placeholder="Enter your password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                        >
                            {showPassword ? "🙈" : "👁️"}
                        </button>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleForgotPassword}
                            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                            Forgot Password?
                        </button>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-2 rounded-lg font-semibold transition-all text-white ${loading
                                ? "bg-indigo-400 cursor-not-allowed"
                                : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                    >
                        {loading ? "Signing In..." : "Sign In"}
                    </button>
                </form>

                {error && (
                    <p className="mt-4 text-center text-red-600 font-medium">{error}</p>
                )}

                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-700">
                        Don’t have an account?{" "}
                        <button
                            onClick={() => navigate("/signup")}
                            className="text-indigo-600 font-semibold hover:underline"
                        >
                            Create Account
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
